import { QueryClient, useMutation, useQuery, useQueryClient } from 'react-query';
import { api, TMSResponse } from './service-api';
import { USERSTATUS, USERTYPE } from 'common/enum';
import { PlaceType, TraineeProfile } from 'pages/private/TraineeProfile';
import { httpClient } from 'redux/reducers/settings';
import { IPersonalDetails } from 'pages/user-private-route/TraineeProfile/PersonalInfo/Schema';
import { IEducationDetails } from '../types/EducationDetail';
import { toast } from 'react-toastify';
import { IContactDetails } from '../types/ContactDetail';
import { IExtraDetails } from '../types/ExtraDetail';
import { IExperience } from '../types/TrainneExp';
import { ITraining } from '../types/Training';
import { IJobDetails } from '../types/JobDetaill';

export interface IUserDetail {
  id: string;
  first_name: string;
  last_name: string;
  image: string;
  role_details: string;
  message: string;
  status: string;
  statusCode: string;
  user_details: {
    attendance_pin: string;
    email: string;
    id: number;
    user_type: USERTYPE;
    status: USERSTATUS;
  };
}

export interface TraineeProfileDetail {
  id: number;
  user: number;
  first_name: string;
  middle_name: string;
  last_name: string;
  signature: string;
  image: string;
  mobile_number: string;
  first_name_np: string;
  middle_name_np: string;
  last_name_np: string;
  dob: string;
  address: Address;
  current_training: any;
  previous_training: PreviousTraining[];
  experience: Experience[];
  ethnicity: string;
  service_group: string;
}

export interface Address {
  id: number;
  province: Province;
  district: District;
  municipality: string;
  ward_no: number;
  house_num: string;
  tole_street: string;
  block_no: string;
}

export interface SuccessResponse {
  data: unknown;
  message: string;
  status: string;
}

export interface Province {
  name_en: string;
  name_np: string;
}
export interface District {
  name_en: string;
  name_np: string;
}

export interface PreviousTraining {
  id: number;
  name: string;
  from_date: string;
  to_date: string;
  place_type: PlaceType;
  place: string;
}

export interface Experience {
  id: number;
  from_date: string;
  to_date: string;
  organization: string;
  designation: any;
  department: string;
  level: string;
  is_first_service: boolean;
  is_current_service: boolean;
}

const fetchUserDetail = () => {
  return httpClient.get<TMSResponse<IUserDetail>>(api.userDetail);
};

const useCurrentUserDetailsQuery = () => {
  return useQuery(api.userDetail, fetchUserDetail, {
    select: ({ data }) => {
      return data.data;
    }
  });
};

const fetchTraineeDetail = (traineeId: string) => () => {
  return httpClient.get<TraineeProfileDetail>(api.trainee.detail.replace('{traineeId}', traineeId));
};

const useTraineeDetailQuery = (traineeId: string) => {
  return useQuery([api.trainee.detail, traineeId], fetchTraineeDetail(traineeId), {
    enabled: !!traineeId,
    select: ({ data }) => {
      return data;
    }
  });
};

const updateTraineeDetail = (traineeId: string) => (traineeData: Partial<TraineeProfile>) => {
  const formData = new FormData();
  for (const data in traineeData) {
    const value = traineeData[data as keyof TraineeProfile];
    formData.append(
      data,
      value instanceof File ? value : value instanceof Object ? JSON.stringify(value) : value ?? ''
    );
  }

  return httpClient.patch<TMSResponse<TraineeProfileDetail>>(
    api.trainee.update.replace('{traineeId}', traineeId),
    formData
  );
};

const useUpdateTraineeDetailMutation = (traineeId: string) => {
  const queryClient = useQueryClient();

  return useMutation([api.trainee.detail, traineeId], updateTraineeDetail(traineeId));
};

const updatePersonalDetail = (traineeId: string) => (personalDetail: IPersonalDetails) => {
  const formData = new FormData();
  for (const data in personalDetail) {
    const value = personalDetail[data as keyof IPersonalDetails] as string | Blob;

    if (value === undefined) continue;

    formData.append(
      data,
      value instanceof File ? value : value instanceof Object ? JSON.stringify(value) : value ?? ''
    );
  }
  return httpClient.patch<TMSResponse<IPersonalDetails>>(
    api.trainee.update_personal_detail.replace('{traineeId}', traineeId),
    formData
  );
};

const useUpdatePersonalDetailMutation = (traineeId: string) => {
  const queryClient = useQueryClient();
  return useMutation(updatePersonalDetail(traineeId), {
    onSuccess: (res: any) => {
      if (res.data.code == 200) {

        queryClient.invalidateQueries(api.trainee.get_personal_detail);
      } else {
        toast.error(res.data.message ?? "Failed to save");
      }
    },
    onError: (res: any) => {
      toast.error(res.response?.data?.message ?? "Failed to save");

    }
  });
};

const updateEducationDetail = (traineeId: string) => (educationDetail: IEducationDetails) => {
  const formData = new FormData();
  for (const data in educationDetail) {
    const value = educationDetail[data as keyof IEducationDetails] as string | Blob;
    formData.append(
      data,
      value instanceof File ? value : value instanceof Object ? JSON.stringify(value) : value ?? ''
    );
  }
  return httpClient.patch<TMSResponse<IEducationDetails>>(
    api.trainee.update_education_detail.replace('{traineeId}', traineeId),
    formData
  );
};

const useUpdateEducationDetail = (traineeId: string) => {
  const queryClient = useQueryClient();
  return useMutation(updateEducationDetail(traineeId), {
    onSuccess: () => {
      queryClient.invalidateQueries(api.trainee.get_education_detail);
    }
  });
};

const deleteEducationDetail = (params: { traineeId: string; educationId: string }) => {
  return httpClient.delete(
    api.trainee.delete_education_detail
      .replace('{id}', params.traineeId)
      .replace('{education_id}', params.educationId)
  );
};

const useDeleteEducationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(api.trainee.delete_education_detail, deleteEducationDetail, {
    onSuccess: () => {
      queryClient.invalidateQueries(api.trainee.get_education_detail);
      toast.success('Education details deleted Successfully.');
    }
  });
};

const updateContactDetail = (traineeId: string) => (personalDetail: IContactDetails) => {
  return httpClient.patch<TMSResponse<IContactDetails>>(
    api.trainee.update_contact_detail.replace('{traineeId}', traineeId),
    personalDetail
  );
};

const useUpdateContactDetailMutation = (traineeId: string) => {
  return useMutation(updateContactDetail(traineeId));
};

const updateExtraDetail = (traineeId: string) => (extraDetail: IExtraDetails) => {
  return httpClient.patch<TMSResponse<IExtraDetails>>(
    api.trainee.update_extra_detail.replace('{traineeId}', traineeId),
    extraDetail
  );
};

const useUpdateExtraDetailMutation = (traineeId: string) => {
  return useMutation(updateExtraDetail(traineeId));
};

const updateExperienceDetail =
  (params: { traineeId: string; experienceId: string }) => (experienceDetail: IExperience) => {
    return httpClient.patch<TMSResponse<IExperience>>(
      api.trainee.update_experience_detail
        .replace('{id}', params.traineeId)
        .replace('{experience_id}', params.experienceId),
      experienceDetail
    );
  };

const useUpdateExperienceDetailMutation = (traineeId: string, experienceId: string) => {
  const queryClient = useQueryClient();
  return useMutation(updateExperienceDetail({ traineeId, experienceId }), {
    onSuccess: () => {
      queryClient.invalidateQueries(api.trainee.get_experience_detail);
      toast.success('Experience Successfully Updated.');
    }
  });
};

const createExperience = (traineeId: string) => (experienceDetail: IExperience) => {
  return httpClient.post<TMSResponse<IExperience>>(
    api.trainee.create_experience.replace('{id}', traineeId),
    experienceDetail
  );
};

const useCreateExperienceMutation = (traineeId: string) => {
  const queryClient = useQueryClient();
  return useMutation(createExperience(traineeId), {
    onSuccess: () => {
      queryClient.invalidateQueries(api.trainee.get_experience_detail);
      toast.success('Experience Successfully Created.');
    }
  });
};

const createTraining = (traineeId: string) => (trainingDetail: ITraining) => {
  return httpClient.post<TMSResponse<ITraining>>(
    api.trainee.create_training.replace('{id}', traineeId),
    trainingDetail
  );
};

const useCreateTrainingMutation = (traineeId: string) => {
  const queryClient = useQueryClient();
  return useMutation(createTraining(traineeId), {
    onSuccess: () => {
      queryClient.invalidateQueries(api.trainee.get_training_detail);
      toast.success('Training Successfully Created.');
    }
  });
};

const updateTrainingDetail =
  (params: { traineeId: string; trainingId: string }) => (trainingDetail: ITraining) => {
    return httpClient.patch<TMSResponse<ITraining>>(
      api.trainee.update_training_detail
        .replace('{id}', params.traineeId)
        .replace('{training_id}', params.trainingId),
      trainingDetail
    );
  };

const useUpdateTrainingDetailMutation = (traineeId: string, trainingId: string) => {
  const queryClient = useQueryClient();
  return useMutation(updateTrainingDetail({ traineeId, trainingId }), {
    onSuccess: () => {
      queryClient.invalidateQueries(api.trainee.get_training_detail);
      toast.success('Training Successfully Updated.');
    }
  });
};
const updateJobDetail = (traineeId: string) => (jobDetailDetail: IJobDetails) => {
  return httpClient.patch<TMSResponse<IJobDetails>>(
    api.trainee.update_job_detail.replace('{traineeId}', traineeId),
    jobDetailDetail
  );
};

const useUpdateJobDetailMutation = (traineeId: string) => {
  return useMutation(updateJobDetail(traineeId));
};

const fetchpersonalDetail = (traineeId: string) => () => {
  return httpClient.get<any>(api.trainee.get_personal_detail.replace('{traineeId}', traineeId));
};

const usePersonalDetailQuery = (traineeId: string, onSuccess?: (data: any) => void) => {
  return useQuery([api.trainee.get_personal_detail, traineeId], fetchpersonalDetail(traineeId), {
    enabled: !!traineeId,
    select: ({ data }) => {
      return data;
    },
    onSuccess,
    staleTime: 0
  });
};

const fetchContactDetail = (traineeId: string) => () => {
  return httpClient.get<any>(api.trainee.get_contact_detail.replace('{traineeId}', traineeId));
};

const useContactDetailsQuery = (traineeId: string, onSuccess?: (data: any) => void) => {
  return useQuery([api.trainee.get_contact_detail, traineeId], fetchContactDetail(traineeId), {
    enabled: !!traineeId,
    select: ({ data }) => {
      return data;
    },
    onSuccess,
    staleTime: 0
  });
};

const fetchExtraDetail = (traineeId: string) => () => {
  return httpClient.get<any>(api.trainee.get_extra_detail.replace('{traineeId}', traineeId));
};

const useGetExtraDetailQuery = (traineeId: string, onSuccess?: (data: any) => void) => {
  return useQuery([api.trainee.get_extra_detail, traineeId], fetchExtraDetail(traineeId), {
    enabled: !!traineeId,
    select: ({ data }) => {
      return data;
    },
    onSuccess,
    staleTime: 0
  });
};

const fetchExperienceDetail = (traineeId: string) => () => {
  return httpClient.get<any>(api.trainee.get_experience_detail.replace('{traineeId}', traineeId));
};

const useFetchExperienceDetailQuery = (traineeId: string, onSuccess?: (data: any) => void) => {
  return useQuery(
    [api.trainee.get_experience_detail, traineeId],
    fetchExperienceDetail(traineeId),
    {
      enabled: !!traineeId,
      select: ({ data }) => {
        return data;
      },
      onSuccess,
      staleTime: 0
    }
  );
};
const fetchTrainingDetail = (traineeId: string) => () => {
  return httpClient.get<any>(api.trainee.get_training_detail.replace('{id}', traineeId));
};

const useFetchTrainingDetailQuery = (traineeId: string, onSuccess?: (data: any) => void) => {
  return useQuery([api.trainee.get_training_detail, traineeId], fetchTrainingDetail(traineeId), {
    enabled: !!traineeId,
    select: ({ data }) => {
      return data;
    },
    onSuccess,
    staleTime: 0
  });
};

const fetchEducationDetail = (traineeId: string) => () => {
  return httpClient.get<any>(api.trainee.get_education_detail.replace('{traineeId}', traineeId));
};

const useGetEducationDetailQuery = (traineeId: string, onSuccess?: (data: any) => void) => {
  return useQuery([api.trainee.get_education_detail, traineeId], fetchEducationDetail(traineeId), {
    enabled: !!traineeId,
    select: ({ data }) => {
      return data;
    },
    onSuccess,
    staleTime: 0
  });
};

const fetchJobDetail = (traineeId: string) => () => {
  return httpClient.get<any>(api.trainee.get_job_detail.replace('{traineeId}', traineeId));
};

const useGetJobDetailQuery = (traineeId: string, onSuccess?: (data: any) => void) => {
  return useQuery([api.trainee.get_job_detail, traineeId], fetchJobDetail(traineeId), {
    enabled: !!traineeId,
    select: ({ data }) => {
      return data;
    },
    onSuccess,
    staleTime: 0
  });
};

export {
  useCurrentUserDetailsQuery,
  useTraineeDetailQuery,
  useUpdateTraineeDetailMutation,
  useUpdatePersonalDetailMutation,
  usePersonalDetailQuery,
  useUpdateJobDetailMutation,
  useUpdateContactDetailMutation,
  useUpdateEducationDetail,
  useUpdateExtraDetailMutation,
  useUpdateExperienceDetailMutation,
  useContactDetailsQuery,
  useGetExtraDetailQuery,
  useFetchExperienceDetailQuery,
  useFetchTrainingDetailQuery,
  useGetEducationDetailQuery,
  useGetJobDetailQuery,
  useDeleteEducationMutation,
  useCreateExperienceMutation,
  useCreateTrainingMutation,
  useUpdateTrainingDetailMutation
};
